import React from 'react';
import { Button } from './ui/button';
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from './ui/card';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from './ui/table';
import './TokenUsageModal.css';

const PHASE_LABELS = {
  title:           'Title Generation',
  upload_instruction: 'Upload Instructions',
  preprocessing:   'Preprocessing (RLM Agent)',
  '1_understand':  'Understanding Your Data',
  '2_1_classify':  'Deciding Approach & Target',
  '2_2_preprocess':'Pre-processing (Agent Call)',
  '2_3_clean':     'Cleaning for Modeling',
  '2_4_analyze':   'Analysing Data',
  '3_model':       'Training Models',
  '4_results':     'Generating Predictions',
  '4_choice':      'Awaiting Your Choice',
  '5_explain':     'Explaining Predictions',
  '6_insights':    'Actionable Insights',
  '7_qna':         'Q&A',
  '8_1_causal_spec':     'Framing Causal Question',
  '8_2_causal_estimate': 'Estimating Causal Effect',
  notebook_export: 'Notebook Export',
};

const PHASE_ORDER = Object.keys(PHASE_LABELS);

// "rlm:<purpose>:<model>" keys carry the code-interpreter subagent's usage,
// split by what it was doing and priced at the model that actually consumed
// the tokens (may differ from the phase model). "rlm:<model>" is the legacy
// form from before the purpose split.
const RLM_PURPOSE_LABELS = {
  preprocess: 'Preprocessing',
  qna: 'Q&A',
  deep_analysis: 'Deep Analysis',
};

function phaseLabel(key) {
  if (key.startsWith('rlm:')) {
    const rest = key.slice(4);
    const sep = rest.indexOf(':');
    if (sep === -1) return `Code Agent (${rest})`;
    const purpose = rest.slice(0, sep);
    const model = rest.slice(sep + 1);
    return `Code Agent · ${RLM_PURPOSE_LABELS[purpose] || purpose} (${model})`;
  }
  return PHASE_LABELS[key] || key;
}

function fmtNum(n) {
  return (n || 0).toLocaleString();
}

function fmtCost(c) {
  if (c == null) return '-';
  if (c < 0.00005) return '<$0.0001';
  return `$${Number(c).toFixed(4)}`;
}

export default function TokenUsageModal({ isOpen, onClose, tokenUsage }) {
  if (!isOpen) return null;

  const total = tokenUsage?.total || {};
  const byPhase = tokenUsage?.by_phase || {};

  const phases = Object.entries(byPhase)
    .filter(([, v]) => (v.input_tokens || 0) + (v.output_tokens || 0) + (v.cache_read_input_tokens || 0) > 0)
    .sort(([a], [b]) => {
      const ia = PHASE_ORDER.indexOf(a);
      const ib = PHASE_ORDER.indexOf(b);
      return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
    });

  const hasData = phases.length > 0 || total.input_tokens;
  // Only show the cache column once something has actually been cached, so
  // analyses that pre-date prompt caching don't gain an all-zero column.
  const showCached = (total.cache_read_input_tokens || 0) > 0
    || phases.some(([, v]) => (v.cache_read_input_tokens || 0) > 0);

  return (
    <div
      className="token-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <Card className="token-modal__card" onClick={(e) => e.stopPropagation()}>
        <CardHeader>
          <CardTitle>Token Usage &amp; Cost</CardTitle>
          <Button variant="ghost" size="sm" onClick={onClose} aria-label="Close">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </Button>
        </CardHeader>

        <CardContent>
          {!hasData ? (
            <p className="token-modal__empty">
              No token usage recorded for this analysis yet.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Phase</TableHead>
                  <TableHead className="ui-table__num">Input Tokens</TableHead>
                  {showCached && (
                    <TableHead className="ui-table__num" title="Input tokens served from the prompt cache at 10% of the normal price">
                      Cached
                    </TableHead>
                  )}
                  <TableHead className="ui-table__num">Output Tokens</TableHead>
                  <TableHead className="ui-table__num">Cost (USD)</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {phases.map(([key, stats]) => (
                  <TableRow key={key}>
                    <TableCell>{phaseLabel(key)}</TableCell>
                    <TableCell className="ui-table__num">{fmtNum(stats.input_tokens)}</TableCell>
                    {showCached && <TableCell className="ui-table__num">{fmtNum(stats.cache_read_input_tokens)}</TableCell>}
                    <TableCell className="ui-table__num">{fmtNum(stats.output_tokens)}</TableCell>
                    <TableCell className="ui-table__num">{fmtCost(stats.cost_usd)}</TableCell>
                  </TableRow>
                ))}
                <TableRow className="token-modal__total">
                  <TableCell>Total</TableCell>
                  <TableCell className="ui-table__num">{fmtNum(total.input_tokens)}</TableCell>
                  {showCached && <TableCell className="ui-table__num">{fmtNum(total.cache_read_input_tokens)}</TableCell>}
                  <TableCell className="ui-table__num">{fmtNum(total.output_tokens)}</TableCell>
                  <TableCell className="ui-table__num">{fmtCost(total.cost_usd)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          )}
        </CardContent>

        <CardFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </CardFooter>
      </Card>
    </div>
  );
}
